'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '@/components/ui/dialog';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { sendGTMEvent } from '@next/third-parties/google';

interface ModalVideoPlayerProps {
  children: React.ReactElement;
  videoId: string;
  videoTitle: string;
  videoLocation: string;
}

// YouTube IFrame API Type Definitions
export interface YTPlayerInstance {
  destroy: () => void;
  getDuration: () => number;
  getCurrentTime: () => number;
  // Add other methods like playVideo, pauseVideo, stopVideo, getPlayerState if needed
}

export interface YTPlayerOptions {
  videoId: string;
  playerVars?: {
    autoplay?: 0 | 1;
    modestbranding?: 1;
    rel?: 0;
    playsinline?: 1;
    [key: string]: string | number | boolean | undefined | null; // Allows other player variables
  };
  events?: {
    onReady?: (event: YTPlayerEvent) => void;
    onStateChange?: (event: YTOnStateChangeEvent) => void;
  };
  height?: string | number;
  width?: string | number;
}

export interface YTPlayerEvent {
  target: YTPlayerInstance;
}

export interface YTOnStateChangeEvent {
  data: YTPlayerState;
  target: YTPlayerInstance;
}

export type YTPlayerState = number; // Represents the numeric state codes like 0 (ended), 1 (playing), 2 (paused)
// End YouTube IFrame API Type Definitions

declare global {
  interface Window {
    YT: {
      Player: new (
        idOrPlayerContainerRef: string | HTMLDivElement,
        options: YTPlayerOptions
      ) => YTPlayerInstance;
      PlayerState: {
        ENDED: YTPlayerState;
        PLAYING: YTPlayerState;
        PAUSED: YTPlayerState;
        BUFFERING: YTPlayerState;
        CUED: YTPlayerState;
        UNSTARTED?: YTPlayerState; // Typically -1
        [key: string]: YTPlayerState | undefined; // For any other states
      };
    };
    onYouTubeIframeAPIReady: () => void;
    isYouTubeApiLoaded?: boolean;
  }
}

export function ModalVideoPlayer({
  children,
  videoId,
  videoTitle,
  videoLocation,
}: ModalVideoPlayerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const playerRef = useRef<YTPlayerInstance | null>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const trackedMilestonesRef = useRef<{ percent: number; tracked: boolean }[]>([]);

  const resetMilestones = useCallback(() => {
    trackedMilestonesRef.current = [
      { percent: 25, tracked: false },
      { percent: 50, tracked: false },
      { percent: 75, tracked: false },
      { percent: 100, tracked: false },
    ];
  }, []);

  const createPlayer = useCallback(() => {
    if (!playerContainerRef.current) {
      setTimeout(createPlayer, 100);
      return;
    }
    if (playerRef.current) {
      playerRef.current.destroy();
    }

    resetMilestones();

    playerRef.current = new window.YT.Player(playerContainerRef.current!, {
      videoId,
      playerVars: {
        autoplay: 1,
        modestbranding: 1,
        rel: 0,
        playsinline: 1,
      },
      events: {
        onReady: (event: YTPlayerEvent) => {
          sendGTMEvent({
            event: 'video_start',
            video_title: videoTitle,
            video_url: `https://www.youtube.com/watch?v=${videoId}`,
            video_location: videoLocation,
          });

          const duration = event.target.getDuration();
          if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

          progressIntervalRef.current = setInterval(() => {
            if (
              playerRef.current &&
              typeof playerRef.current.getCurrentTime === 'function' &&
              duration > 0
            ) {
              const currentTime = playerRef.current.getCurrentTime();
              const percentWatched = (currentTime / duration) * 100;

              trackedMilestonesRef.current.forEach((milestone) => {
                if (!milestone.tracked && percentWatched >= milestone.percent) {
                  sendGTMEvent({
                    event: 'video_progress',
                    video_title: videoTitle,
                    video_url: `https://www.youtube.com/watch?v=${videoId}`,
                    video_percent: milestone.percent,
                    video_location: videoLocation,
                  });
                  milestone.tracked = true;
                }
              });
            }
          }, 1000);
        },
        onStateChange: (event: YTOnStateChangeEvent) => {
          const playerState = event.data;

          if (playerState === window.YT.PlayerState.PAUSED) {
            sendGTMEvent({
              event: 'video_pause',
              video_title: videoTitle,
              video_url: `https://www.youtube.com/watch?v=${videoId}`,
              video_location: videoLocation,
            });
          }

          if (playerState === window.YT.PlayerState.ENDED) {
            const hundredPercentMilestone = trackedMilestonesRef.current.find(
              (m) => m.percent === 100
            );
            if (hundredPercentMilestone && !hundredPercentMilestone.tracked) {
              sendGTMEvent({
                event: 'video_progress',
                video_title: videoTitle,
                video_url: `https://www.youtube.com/watch?v=${videoId}`,
                video_percent: 100,
                video_location: videoLocation,
              });
              hundredPercentMilestone.tracked = true;
            }

            sendGTMEvent({
              event: 'video_complete',
              video_title: videoTitle,
              video_url: `https://www.youtube.com/watch?v=${videoId}`,
              video_location: videoLocation,
            });
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
          }
        },
      },
    });
  }, [videoId, videoTitle, videoLocation, resetMilestones]);

  useEffect(() => {
    if (!window.isYouTubeApiLoaded) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else {
        document.head.appendChild(tag);
      }
      window.isYouTubeApiLoaded = true;

      if (typeof window.onYouTubeIframeAPIReady !== 'function') {
        window.onYouTubeIframeAPIReady = () => {
          // This function is intentionally kept minimal.
          // The actual player creation is handled by the useEffect [isOpen] hook
          // when a modal is opened and needs a player.
        };
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetMilestones();
      if (window.YT && window.YT.Player) {
        createPlayer();
      } else {
        const originalCallback = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (typeof originalCallback === 'function' && originalCallback.name !== 'createPlayer') {
            // Avoid issues if originalCallback was one of our temp ones
            originalCallback();
          }
          createPlayer();
          // Attempt to restore the original to prevent issues if multiple players exist,
          // but only if it wasn't one of our temporary assignments.
          if (originalCallback && originalCallback.name !== 'createPlayer') {
            window.onYouTubeIframeAPIReady = originalCallback;
          } else if (!originalCallback) {
            // If there was no original, set it to a no-op to prevent errors if called again by YT API
            window.onYouTubeIframeAPIReady = () => {};
          }
        };
        if (window.isYouTubeApiLoaded && !(window.YT && window.YT.Player)) {
          setTimeout(() => {
            if (window.YT && window.YT.Player) createPlayer();
            // If after timeout it's still not there, onYouTubeIframeAPIReady should handle it.
          }, 500);
        } else if (window.isYouTubeApiLoaded && window.YT && window.YT.Player) {
          createPlayer();
        }
      }
    } else {
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.error('Error destroying YouTube player:', e);
        }
        playerRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }
    return () => {
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.error('Error destroying YouTube player (cleanup):', e);
        }
        playerRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isOpen, videoId, videoTitle, videoLocation, createPlayer, resetMilestones]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {React.isValidElement(children) ? (
          React.cloneElement(children, {
            onClick: (e: React.MouseEvent) => {
              const childProps = children.props as { onClick?: (e: React.MouseEvent) => void; style?: React.CSSProperties };
              if (typeof childProps.onClick === 'function') {
                childProps.onClick(e);
              }
              setIsOpen(true);
            },
            style: { cursor: 'pointer', ...((children.props as { style?: React.CSSProperties })?.style || {}) },
          } as any)
        ) : (
          <button onClick={() => setIsOpen(true)}>{children}</button>
        )}
      </DialogTrigger>
      <DialogContent className="overflow-hidden p-0 sm:max-w-[800px]">
        <DialogTitle className="sr-only">{videoTitle}</DialogTitle>
        <AspectRatio ratio={16 / 9}>
          <div
            ref={playerContainerRef}
            id={`ytplayer-${videoId}-${Math.random().toString(36).substring(7)}`}
            className="h-full w-full"
          />
        </AspectRatio>
      </DialogContent>
    </Dialog>
  );
}
